<script setup lang="ts">
import { computed } from 'vue';
import type { TaskItem } from '@Types/dashboard.types';
import type {
    PaymentCompletionTaskData,
    PaymentConfirmation,
} from '@Types/completionTaskData.types';
import TaskCompletionHero from '@Components/completion/TaskCompletionHero.vue';
import CompletionSummaryCard from '@Components/completion/CompletionSummaryCard.vue';
import CompletionSummaryRow from '@Components/completion/CompletionSummaryRow.vue';
import IllustrationHousePayment from '@Components/priming/illustrations/IllustrationHousePayment.vue';
import { formatLongDate } from '@Src/shared/utils/formatDate';

const props = defineProps<{
    task: TaskItem;
}>();

const data = computed<PaymentCompletionTaskData>(() =>
    (props.task.taskData ?? {}) as PaymentCompletionTaskData,
);
const confirmation = computed<PaymentConfirmation | null>(
    () => data.value.payment_confirmation ?? null,
);

// Detect repayment before the payload payment_type check: a repayment payload may be
// 'cash_to_close', which would otherwise misclassify it as c2c.
const mode = computed<'emd' | 'c2c' | 'repayment'>(() => {
    if (props.task.taskType === 'payment_repayment') return 'repayment';
    const payloadType = data.value.payment_details?.payment_type;
    if (payloadType === 'cash_to_close') return 'c2c';
    if (payloadType === 'earnest_money_deposit') return 'emd';
    return props.task.taskType === 'payment_c2c' ? 'c2c' : 'emd';
});

// C2C wire fallback — user chose wire instead of digital; confirmation
// payload is absent because the funds moved outside our rails.
const isWireFallback = computed(
    () => mode.value === 'c2c' && data.value.payment_method_choice === 'wire',
);

// paymentVisualStatus: derived from estimated_arrival vs now. If the ACH/
// instant rail hasn't settled yet, show "in transit"; once the arrival
// date has passed (or is unknown), show "delivered". Wire fallback is
// always delivered — wire moves same-day.
//
// When the BFF eventually exposes a canonical payment status field, swap
// this derivation for that read.
const visualStatus = computed<'pending' | 'completed'>(() => {
    // Wire moves same-day and carries no confirmation payload by design.
    if (isWireFallback.value) return 'completed';

    // An absent or unparseable ETA means we do not know yet, not that the money landed,
    // and estimated_arrival is null on two of the three submit paths. Claiming a delivery
    // that has not happened is the worse of the two errors.
    const eta = confirmation.value?.estimated_arrival;
    if (!eta) return 'pending';
    const etaTime = Date.parse(eta);
    if (Number.isNaN(etaTime)) return 'pending';

    return etaTime > Date.now() ? 'pending' : 'completed';
});

const heroHeading = computed(() => {
    if (mode.value === 'repayment') {
        return visualStatus.value === 'pending' ? 'Your payment is on its way' : 'Your repayment is complete';
    }
    if (visualStatus.value === 'pending') {
        return 'Your payment is on its way';
    }
    return mode.value === 'c2c' ? 'Your closing funds are delivered' : 'Your earnest money is delivered';
});

const bodyMessage = computed(() => {
    if (mode.value === 'repayment' && visualStatus.value === 'completed') {
        return 'The title company has received your repayment. Keep this confirmation for your records.';
    }
    if (visualStatus.value === 'pending') {
        return 'We’ve received your payment and it’s headed to the title company now. You’ll see a confirmation here once it settles.';
    }
    return 'The title company has received your payment. Keep this confirmation for your records.';
});

const companyName = computed(() =>
    data.value.payment_request?.company_name || 'the title company',
);

const currency = (n?: number) =>
    typeof n === 'number'
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
        : '—';

const purposeLabel = computed(() => {
    if (mode.value === 'repayment') return 'Repayment';
    return mode.value === 'c2c' ? 'Cash to Close' : 'Earnest Money Deposit';
});

const paymentMethodLabel = computed(() => {
    const c = confirmation.value;
    if (!c?.bank_name) return '';
    const last4 = c.bank_last_four ? ` •••• ${c.bank_last_four}` : '';
    // Account type omitted until the BFF supplies it — hardcoding
    // "Checking" here would mislabel savings-account confirmations.
    return `${c.bank_name}${last4}`;
});

const processingFee = computed(() => confirmation.value?.processing_fee ?? 0);
const totalAmount = computed(() => {
    const c = confirmation.value;
    if (!c) return 0;
    if (typeof c.total === 'number') return c.total;
    return (c.amount ?? 0) + (c.processing_fee ?? 0);
});
</script>

<template>
    <div class="pcv">
        <TaskCompletionHero :status-icon="visualStatus === 'completed' ? 'success' : 'pending'">
            <template #illustration>
                <IllustrationHousePayment />
            </template>
            {{ heroHeading }}
        </TaskCompletionHero>

        <div class="pcv__body">
            <!-- Wire fallback: skip the transaction card; surface a minimal
                 acknowledgment since digital confirmation data isn't available. -->
            <template v-if="isWireFallback">
                <p class="pcv__message">
                    You elected to send this payment by wire. Funds are moving outside CertifID’s
                    rails, so there’s no transaction record to show here. Please keep your bank’s
                    wire confirmation for your records.
                </p>
            </template>

            <template v-else>
                <p class="pcv__message">{{ bodyMessage }}</p>

                <CompletionSummaryCard heading="Payment details">
                    <div class="pcv__amount-row">
                        <span class="pcv__amount-value">{{ currency(confirmation?.amount) }}</span>
                        <span
                            class="pcv__status-pill"
                            :class="`pcv__status-pill--${visualStatus}`"
                        >
                            {{ visualStatus === 'completed' ? 'Delivered' : 'In transit' }}
                        </span>
                    </div>

                    <div class="pcv__divider" />

                    <div v-if="confirmation?.processed_at" class="pcv__dates">
                        <div class="pcv__date-col">
                            <p class="pcv__row-label">Submitted</p>
                            <p class="pcv__row-value">{{ formatLongDate(confirmation.processed_at) }}</p>
                        </div>
                        <div
                            v-if="visualStatus === 'completed' && confirmation.estimated_arrival"
                            class="pcv__date-col"
                        >
                            <p class="pcv__row-label">Delivered</p>
                            <p class="pcv__row-value">{{ formatLongDate(confirmation.estimated_arrival) }}</p>
                        </div>
                        <div
                            v-else-if="visualStatus === 'pending' && confirmation.estimated_arrival"
                            class="pcv__date-col"
                        >
                            <p class="pcv__row-label">Arrives</p>
                            <p class="pcv__row-value">{{ formatLongDate(confirmation.estimated_arrival) }}</p>
                        </div>
                    </div>

                    <div v-if="confirmation?.processed_at" class="pcv__divider" />

                    <div class="pcv__rows">
                        <CompletionSummaryRow v-if="confirmation?.property_address" label="Property">
                            {{ confirmation.property_address }}
                        </CompletionSummaryRow>
                        <CompletionSummaryRow label="Purpose">{{ purposeLabel }}</CompletionSummaryRow>
                        <CompletionSummaryRow v-if="confirmation?.confirmation_number" label="Transaction ID" mono>
                            {{ confirmation.confirmation_number }}
                        </CompletionSummaryRow>
                        <CompletionSummaryRow v-if="paymentMethodLabel" label="Payment method">
                            {{ paymentMethodLabel }}
                        </CompletionSummaryRow>
                    </div>

                    <div v-if="confirmation" class="pcv__divider" />

                    <div v-if="confirmation" class="pcv__totals">
                        <div class="pcv__total-row">
                            <span>Payment amount</span>
                            <span>{{ currency(confirmation.amount) }}</span>
                        </div>
                        <div class="pcv__total-row">
                            <span>Processing fee</span>
                            <span>{{ currency(processingFee) }}</span>
                        </div>
                        <div class="pcv__total-row pcv__total-row--sum">
                            <span>Total</span>
                            <span>{{ currency(totalAmount) }}</span>
                        </div>
                    </div>
                </CompletionSummaryCard>

                <!-- Hidden until the receipt PDF exists. DownloadCard's click handler only
                     logs to the console pending the document-download BFF contract, so shipping
                     it visible offers a button that silently does nothing. -->
            </template>

            <p class="pcv__contact">
                Questions about this payment? Reach out to {{ companyName }}.
            </p>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.pcv {
    display: flex;
    flex-direction: column;

    &__body {
        display: flex;
        flex-direction: column;
        gap: 20px;
        padding: 20px $portal-section-px 0;

        @media (min-width: 768px) {
            padding: 20px 32px 0;
        }
    }

    &__message {
        font-size: $text-portal-body-lg;
        color: $portal-text;
        line-height: 1.5;
        margin: 0;
    }

    &__amount-row {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__amount-value {
        flex: 1;
        font-size: 24px;
        font-weight: 700;
        color: $portal-text;
        line-height: 1.3;
    }

    &__status-pill {
        flex-shrink: 0;
        font-size: $text-portal-small;
        font-weight: 700;
        padding: 4px 8px;
        border-radius: $portal-tag-radius;
        white-space: nowrap;

        &--pending {
            background: #FFF4D0;
            color: #322705;
        }

        &--completed {
            background: #CBF5DC;
            color: #0B351C;
        }
    }

    &__divider {
        height: 1px;
        background: $portal-border;
    }

    &__dates {
        display: flex;
        gap: 16px;
    }

    &__date-col {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    &__rows {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    // Typography shared with CompletionSummaryRow; kept local because the
    // date columns use the same label/value pair inside a flex row layout.
    &__row-label {
        font-size: $text-portal-small;
        color: $portal-text-mid;
        margin: 0;
    }

    &__row-value {
        font-size: $text-portal-caption;
        font-weight: 700;
        color: $portal-text;
        margin: 0;
        line-height: 1.4;
    }

    &__totals {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    &__total-row {
        display: flex;
        justify-content: space-between;
        font-size: $text-portal-caption;
        color: $portal-text-mid;

        &--sum {
            font-weight: 700;
            color: $portal-text;
        }
    }

    &__contact {
        font-size: $text-portal-small;
        color: $portal-text-mid;
        text-align: center;
        line-height: 1.5;
        margin: 0;
    }
}
</style>
